import { Button } from '@dropbox/dig-components/buttons';
import {
  FormHelperText,
  FormLabel,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { Modal } from '@dropbox/dig-components/modal';
import { TextArea, TextInput } from '@dropbox/dig-components/text_fields';
import { Text } from '@dropbox/dig-components/typography';
import { Box } from '@dropbox/dig-foundations';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SignerReassignmentInfo } from 'signer-app/context/signer-app-client/signer-api';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import {
  postMessage,
  messages as postMessageTypes,
} from 'signer-app/utils/messages';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { atom, useSetAtom } from 'jotai';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import { LineDivider } from 'hello-react/components/line-divider';

type Props = {
  signatureRequestId: string;
  tsmGuid: string;
  closeModal: () => void;
  isOpen: boolean;
  embeddedParentUrl?: string;
  accessCode?: string;
};
function isValidEmailAddress(email: string) {
  const re =
    /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export const signerReassignmentInfoAtom = atom(
  undefined as undefined | SignerReassignmentInfo,
);

export function ReassignModal({
  closeModal: abort,
  isOpen,
  tsmGuid,
  embeddedParentUrl,
  signatureRequestId,
  accessCode,
}: Props) {
  const setStoredData = useSetAtom(signerReassignmentInfoAtom);
  const history = useHistory();
  const client = useSignerAppClient();
  const reassignMutation = useMutation({
    mutationKey: ['signature-request', 'reassign'],
    mutationFn: async (values: SignerReassignmentInfo) => {
      try {
        await client.signerApi.reassign(
          values.tsmGuid,
          values.newSignerName,
          values.newSignerEmail,
          values.reason,
          values.accessCode ?? undefined,
        );
        if (embeddedParentUrl) {
          // Embedded V2
          postMessage(
            {
              type: postMessageTypes.USER_REASSIGN_REQUEST,
              payload: {
                name: values.newSignerName,
                email: values.newSignerEmail,
                reason: values.reason,
                signatureId: values.tsmGuid,
              },
            },
            embeddedParentUrl,
          );
        }
        // Data is only stored in the Atom if it has passed validation and is
        // proceeding to the next step.
        setStoredData(values);
        return history.push(`/sign/${signatureRequestId}/delegateComplete/`);
      } catch (e) {
        const error = new Error('Delegation failed.') as Error & {
          code?: string;
        };
        error.code = 'server_error';

        if (embeddedParentUrl) {
          // Embedded V2
          postMessage(
            {
              type: postMessageTypes.APP_ERROR,
              payload: {
                code: error.code,
                signatureId: values.tsmGuid,
              },
            },
            embeddedParentUrl,
          );
        }
        throw e;
      }
    },
  });

  const [data, setData] = React.useState<SignerReassignmentInfo>({
    tsmGuid,
    newSignerName: '',
    newSignerEmail: '',
    reason: '',
    accessCode: accessCode ?? null,
  });
  type FormErrors = Partial<Record<keyof SignerReassignmentInfo, boolean>>;

  const [errors, setErrors] = React.useState<FormErrors>({});
  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement> | React.SyntheticEvent) => {
      event.preventDefault();
      const errors: FormErrors = {};
      if (!data.newSignerName) {
        errors.newSignerName = true;
      }
      if (!data.newSignerEmail || !isValidEmailAddress(data.newSignerEmail)) {
        errors.newSignerEmail = true;
      }
      if (data.reason.trim().length === 0) {
        errors.reason = true;
      }
      console.log('errors', errors, Object.keys(errors).length, data);

      setErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
      return reassignMutation.mutateAsync(data);
    },
    [data, reassignMutation],
  );

  const isReassigning = reassignMutation.isLoading;
  const onRequestClose = () => {
    if (!isReassigning) {
      setErrors({});
      abort();
    }
  };

  return (
    <Modal
      open={isOpen}
      isCentered
      withCloseButton="Close"
      onRequestClose={onRequestClose}
      aria-labelledby="reassign-modal"
    >
      <form
        onSubmit={handleSubmit}
        noValidate
        onChange={(e) => {
          if (!isReassigning) {
            const target = e.target as HTMLInputElement;
            setData((prev) => ({ ...prev, [target.name]: target.value }));
          }
        }}
      >
        <Modal.Header hasBottomSpacing="title-standard">
          <Modal.Title id="reassign-modal">
            <FormattedMessage
              id="signer.delegate.modal.title"
              description="modal header text in signer flow, where signer can reassign the signature request to someone else"
              defaultMessage="Reassign this document"
            />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Box marginBottom="Micro XLarge">
            <Text variant="paragraph">
              <FormattedMessage
                id="signer.delegate.modal.description"
                description="modal text in signer flow asking user to reassign incase singer doesn't have authrization"
                defaultMessage="If you’re not the authorized signer for this document, you can assign it to someone else. By reassigning, the document will be cleared of anything you have entered."
              />
            </Text>
          </Box>
          <FormRow split>
            <FormRow>
              <FormLabel htmlFor="newSignerName">
                <FormattedMessage
                  id="84b2f07525bdd51447fccb53140aacddff18b769002d2a27427d8ec78bb683d7"
                  description="label for new signer name"
                  defaultMessage="Name"
                />
              </FormLabel>
              <TextInput
                id="newSignerName"
                name="newSignerName"
                required
                aria-describedby="newSignerName-error"
                autoFocus
              />
              {errors.newSignerName && (
                <FormHelperText variant="alert" id="newSignerName-error">
                  <FormattedMessage
                    id="signer.delegate.modal.noNameError"
                    description="error message in modal of signer flow, asking singer to provide the name"
                    defaultMessage="Please provide a name."
                  />
                </FormHelperText>
              )}
            </FormRow>
            <FormRow>
              <FormLabel htmlFor="newSignerEmail">
                <FormattedMessage
                  id="914d4eb5b1417fc0c0e31f9c356de6363e17f9305e6db4be2487c234e2e6983a"
                  description="label for new signer email"
                  defaultMessage="Email"
                />
              </FormLabel>
              <TextInput
                type="email"
                id="newSignerEmail"
                name="newSignerEmail"
                aria-describedby="newSignerEmail-error"
                required
              />
              {errors.newSignerEmail && (
                <FormHelperText variant="alert" id="newSignerEmail-error">
                  <FormattedMessage
                    id="signer.delegate.modal.noEmailError"
                    description="error message in modal of signer flow, asking signer to input a valid email"
                    defaultMessage="Please provide a valid email."
                  />
                </FormHelperText>
              )}
            </FormRow>
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="reason">
              <FormattedMessage
                id="signer.delegate.modal.reasonTitle"
                description="label of input of modal in signer flow, asking signer to give reason for reassigning signature request to different user"
                defaultMessage="Reason for reassigning"
              />
            </FormLabel>
            <TextArea
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              id="reason"
              name="reason"
              required
              aria-describedby="reason-error shared-with-sender"
            />
            <FormHelperText id="shared-with-sender">
              <FormattedMessage
                id="signer.delegate.modal.sharedOnlyWithSender"
                description="text in modal of singer flow, informing that all the information will only be between requester and recipient while reassigning the signature request"
                defaultMessage="This information will only be shared with the requester and the recipient."
              />
            </FormHelperText>
            {errors.reason && (
              <FormHelperText variant="alert" id="reason-error">
                <FormattedMessage
                  id="signer.delegate.modal.noReasonError"
                  description="error message in modal of signer flow, asking signer to give reason for reassigning the signature request"
                  defaultMessage="Please provide a reason."
                />
              </FormHelperText>
            )}
          </FormRow>
          <LineDivider />
          <Box marginTop="Micro XLarge">
            <Text variant="paragraph" size="xsmall" color="subtle">
              <FormattedMessage
                id="signer.delegate.modal.ctaMessage"
                description="text in modal of signer flow, informing singer that once reassigned of signature request the document will no longer be accessible and will be notified by email once request is complete"
                defaultMessage="In completing this step, the requester and the recipient will receive a notification of the changes.  You will no longer have access to sign this document and you will receive a copy of the completed document once all parties have signed."
              />
            </Text>
          </Box>
        </Modal.Body>

        <Modal.Footer>
          <WhiteLabelThemeProvider mode="bright">
            <Box display="flex">
              <Button
                variant="opacity"
                type="button"
                onClick={onRequestClose}
                disabled={isReassigning}
              >
                <FormattedMessage
                  id="2422e7feaf09e3809a3bda22e6efc481e26397c00e0322c449623300f2f4e1ff"
                  description="Cancel modal action button"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button variant="primary" type="submit" disabled={isReassigning}>
                <FormattedMessage
                  id="4451ac6b805ef897ed3f329badbc459f04bc2bbc95dff907973a24d92c173f1e"
                  description="Reassign modal action button"
                  defaultMessage="Reassign"
                />
              </Button>
            </Box>
          </WhiteLabelThemeProvider>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
